<template>
  <div class="area-chart-container">
    <div class="chart-header">
      <h6 class="chart-title">{{ title }}</h6>
      <div class="chart-legend" v-if="showLegend">
        <span class="legend-item">
          <span class="legend-dot"></span>
          {{ legendLabel }}
        </span>
      </div>
    </div>
    <div class="chart-wrapper">
      <canvas :id="chartId" ref="chartCanvas"></canvas>
    </div>
    <div class="chart-summary" v-if="summary">
      <div class="summary-item">
        <span class="summary-value">{{ summary.avg }}</span>
        <span class="summary-label">Promedio</span>
      </div>
      <div class="summary-item">
        <span class="summary-value">{{ summary.min }}</span>
        <span class="summary-label">Mínimo</span>
      </div>
      <div class="summary-item">
        <span class="summary-value">{{ summary.max }}</span>
        <span class="summary-label">Máximo</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

export default {
  name: "AreaChartGradient",
  props: {
    title: {
      type: String,
      default: "Tiempo promedio por día",
    },
    labels: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    legendLabel: {
      type: String,
      default: "Tiempo promedio (minutos)",
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: Object,
      default: null,
    },
    gradientFrom: {
      type: String,
      default: "rgba(204, 11, 122, 0.4)",
    },
    gradientTo: {
      type: String,
      default: "rgba(204, 11, 122, 0.02)",
    },
    lineColor: {
      type: String,
      default: "#CC0B7A",
    },
    formatTooltip: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      chartId: `area-chart-${Math.floor(Math.random() * 100000)}`,
      chartInstance: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.createChart();
    });
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    labels: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
  },
  methods: {
    createChart() {
      const canvas = this.$refs.chartCanvas;
      if (!canvas) {
        return;
      }
      if (!this.labels || !this.data || this.labels.length === 0) {
        return;
      }

      const ctx = canvas.getContext("2d");

      // Crear gradiente
      const gradient = ctx.createLinearGradient(0, 0, 0, 300);
      gradient.addColorStop(0, this.gradientFrom);
      gradient.addColorStop(1, this.gradientTo);

      const self = this;

      this.chartInstance = new Chart(ctx, {
        type: "line",
        data: {
          labels: this.labels,
          datasets: [
            {
              label: this.legendLabel,
              data: this.data,
              fill: true,
              backgroundColor: gradient,
              borderColor: this.lineColor,
              borderWidth: 2,
              tension: 0.4,
              pointBackgroundColor: this.lineColor,
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: this.lineColor,
              pointHoverBorderColor: "#fff",
              pointHoverBorderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: "index",
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              titleColor: "#fff",
              bodyColor: "#fff",
              borderColor: this.lineColor,
              borderWidth: 1,
              cornerRadius: 8,
              padding: 12,
              displayColors: false,
              callbacks: {
                label: function (context) {
                  if (self.formatTooltip) {
                    return self.formatTooltip(context.raw);
                  }
                  return `${context.raw} min`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                color: "#6b7280",
                font: {
                  size: 11,
                },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                color: "rgba(0, 0, 0, 0.05)",
              },
              ticks: {
                color: "#6b7280",
                font: {
                  size: 11,
                },
                callback: function (value) {
                  if (self.formatTooltip) {
                    return self.formatTooltip(value);
                  }
                  // Formatear como tiempo (asumiendo segundos)
                  if (value >= 86400) {
                    return `${Math.round(value / 86400)}d`;
                  } else if (value >= 3600) {
                    return `${Math.round(value / 3600)}h`;
                  } else if (value >= 60) {
                    return `${Math.round(value / 60)}m`;
                  }
                  return `${Math.round(value)}s`;
                },
              },
            },
          },
        },
      });
    },
    updateChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
      this.$nextTick(() => {
        this.createChart();
      });
    },
  },
};
</script>

<style scoped lang="scss">
.area-chart-container {
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.chart-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.chart-legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #cc0b7a, #97055d);
}

.chart-wrapper {
  height: 280px;
  position: relative;
}

.chart-summary {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f3f4f6;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.summary-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.summary-label {
  font-size: 0.75rem;
  color: #6b7280;
}

@media (max-width: 768px) {
  .chart-wrapper {
    height: 220px;
  }

  .chart-summary {
    gap: 1rem;
  }

  .summary-value {
    font-size: 1rem;
  }
}
</style>
